/**
 * OrganizationDetail — Admin page for managing a specific org.
 * Route: /admin/organizations/:id
 * Tabs: Overview | Import | Report Preview
 */

import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/useToast';
import { db } from '@/lib/firebase';
import {
  doc,
  getDoc,
  setDoc,
  serverTimestamp,
  Timestamp,
} from 'firebase/firestore';
import type { ParsedRider } from '@/pages/admin/WishIntelligence';
import {
  Upload, CheckCircle, X, AlertCircle, ExternalLink, Copy, ChevronLeft,
} from 'lucide-react';

// ─── Types ────────────────────────────────────────────────────────────────────

interface Org {
  id: string;
  name: string;
  type: 'school' | 'camp';
  slug: string;
  season: string;
  description: string;
  contactEmail: string;
  accessCode: string;
  createdAt: Date;
  updatedAt: Date;
}

interface SchoolInquiry {
  studentFirstName: string;
  studentLastName: string;
  grade: string;
  guardianName: string;
  guardianEmail: string;
  guardianPhone: string;
  pickupAddress: string;
  pickupZipcode: string;
  daysOrWeeks: string;
  notes: string;
}

type TabId = 'overview' | 'import' | 'preview';

// ─── CSV Parsing (copied from WishIntelligence) ───────────────────────────────

function parseCSV(text: string): Record<string, string>[] {
  const lines = text.split(/\r?\n/).filter(l => l.trim() !== '');
  if (lines.length < 2) return [];
  const headers = parseCSVRow(lines[0]);
  const rows: Record<string, string>[] = [];
  for (let i = 1; i < lines.length; i++) {
    const values = parseCSVRow(lines[i]);
    if (values.length === 0) continue;
    const row: Record<string, string> = {};
    headers.forEach((h, idx) => { row[h] = values[idx] ?? ''; });
    rows.push(row);
  }
  return rows;
}

function parseCSVRow(line: string): string[] {
  const result: string[] = [];
  let current = '';
  let inQuotes = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (ch === '"') {
      if (inQuotes && line[i + 1] === '"') { current += '"'; i++; }
      else { inQuotes = !inQuotes; }
    } else if (ch === ',' && !inQuotes) {
      result.push(current); current = '';
    } else {
      current += ch;
    }
  }
  result.push(current);
  return result;
}

function parseWeeks(raw: string): number[] {
  if (!raw || raw.trim() === '') return [];
  return raw.split(',').map(s => parseInt(s.trim(), 10)).filter(n => !isNaN(n));
}

function parseRate(raw: string): 165 | 75 {
  const n = parseInt(raw.replace(/[^0-9]/g, ''), 10);
  return n === 75 ? 75 : 165;
}

function isSkippableTime(val: string): boolean {
  return val.trim().toLowerCase().includes('please let us know');
}

function parseBookedCSV(rows: Record<string, string>[]): ParsedRider[] {
  return rows.map(row => {
    const rate = parseRate(row['Weekly Rate Per Student'] ?? '');
    return {
      name: row['Guardian Name'] ?? '',
      email: row['Guardian Email'] ?? '',
      phone: row['Guardian Phone'] ?? undefined,
      pickup: row['Pickup Location'] ?? '',
      pickupTime: row['Pickup Time'] ?? undefined,
      dropoffTime: row['Dropoff Time'] ?? undefined,
      students: parseInt(row['Students'] ?? '1', 10) || 1,
      weeks: parseWeeks(row['Enrolled Weeks'] ?? ''),
      rate,
      loop: rate === 75 ? 'late' : 'early',
      status: 'booked' as const,
      routeName: row['Route Name'] ?? undefined,
    };
  }).filter(r => r.name || r.email);
}

function parseInterestedCSV(rows: Record<string, string>[]): ParsedRider[] {
  const seen = new Set<string>();
  const results: ParsedRider[] = [];
  for (const row of rows) {
    const email = row['Recipient Email'] ?? '';
    if (!email || seen.has(email)) continue;
    seen.add(email);
    const rate = parseRate(row['Weekly Rate'] ?? '');
    const pickupTimeRaw = row['Pickup Time'] ?? '';
    const dropoffTimeRaw = row['Dropoff Time'] ?? '';
    results.push({
      name: row['Recipient Name'] ?? '',
      email,
      pickup: row['Pickup Location'] ?? '',
      pickupTime: isSkippableTime(pickupTimeRaw) ? undefined : pickupTimeRaw || undefined,
      dropoffTime: isSkippableTime(dropoffTimeRaw) ? undefined : dropoffTimeRaw || undefined,
      students: parseInt(row['Students'] ?? '1', 10) || 1,
      weeks: parseWeeks(row['Selected Weeks'] ?? ''),
      rate,
      loop: rate === 75 ? 'late' : 'early',
      status: 'interested' as const,
      routeName: row['Route Name'] ?? undefined,
    });
  }
  return results;
}

function parseSchoolCSV(rows: Record<string, string>[]): SchoolInquiry[] {
  return rows.map(row => ({
    studentFirstName: row['Student First Name'] ?? '',
    studentLastName: row['Student Last Name'] ?? '',
    grade: row['Grade'] ?? '',
    guardianName: row['Guardian Name'] ?? '',
    guardianEmail: row['Guardian Email'] ?? '',
    guardianPhone: row['Guardian Phone'] ?? '',
    pickupAddress: row['Pickup Address'] ?? '',
    pickupZipcode: row['Pickup Zipcode'] ?? '',
    daysOrWeeks: row['Days / Weeks'] ?? '',
    notes: row['Notes'] ?? '',
  })).filter(r => r.guardianEmail || r.studentFirstName);
}

// ─── Upload Zone ──────────────────────────────────────────────────────────────

function UploadZone({
  label,
  description,
  fileName,
  rowCount,
  error,
  onFile,
  onClear,
  color,
}: {
  label: string;
  description: string;
  fileName: string | null;
  rowCount: number;
  error: string | null;
  onFile: (file: File) => void;
  onClear: () => void;
  color: 'blue' | 'green';
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const colorMap = {
    blue: {
      border: 'border-blue-300 hover:border-blue-400',
      bg: 'bg-blue-50 hover:bg-blue-100',
      text: 'text-blue-700',
    },
    green: {
      border: 'border-green-300 hover:border-green-400',
      bg: 'bg-green-50 hover:bg-green-100',
      text: 'text-green-700',
    },
  };
  const c = colorMap[color];

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file) onFile(file);
  };

  return (
    <div className="flex-1">
      <div className="text-sm font-semibold text-gray-700 mb-2">{label}</div>
      <div
        className={`relative border-2 border-dashed rounded-lg p-6 text-center transition-colors cursor-pointer ${
          fileName ? 'border-gray-300 bg-white' : `${c.border} ${c.bg}`
        }`}
        onDragOver={e => e.preventDefault()}
        onDrop={handleDrop}
        onClick={() => !fileName && inputRef.current?.click()}
      >
        <input
          ref={inputRef}
          type="file"
          accept=".csv"
          className="hidden"
          onChange={e => {
            const file = e.target.files?.[0];
            if (file) onFile(file);
            e.target.value = '';
          }}
        />
        {fileName ? (
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-2 min-w-0">
              <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
              <div className="text-left min-w-0">
                <div className="text-sm font-medium text-gray-900 truncate">{fileName}</div>
                <div className="text-xs text-gray-500">{rowCount} records parsed</div>
              </div>
            </div>
            <button
              onClick={e => { e.stopPropagation(); onClear(); }}
              className="p-1 hover:bg-gray-100 rounded flex-shrink-0"
            >
              <X className="w-4 h-4 text-gray-500" />
            </button>
          </div>
        ) : (
          <>
            <Upload className={`w-8 h-8 mx-auto mb-2 ${c.text}`} />
            <div className={`text-sm font-medium ${c.text}`}>Drop CSV here or click to browse</div>
            <div className="text-xs text-gray-500 mt-1">{description}</div>
          </>
        )}
      </div>
      {error && (
        <div className="mt-2 flex items-start gap-1.5 text-xs text-red-700 bg-red-50 border border-red-200 rounded-lg p-2">
          <AlertCircle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
          {error}
        </div>
      )}
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

export default function OrganizationDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { role, loading: authLoading } = useAuth();
  const { toast } = useToast();

  const [org, setOrg] = useState<Org | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<TabId>('overview');

  // Camp import state
  const [bookedFile, setBookedFile] = useState<string | null>(null);
  const [bookedRiders, setBookedRiders] = useState<ParsedRider[]>([]);
  const [bookedError, setBookedError] = useState<string | null>(null);
  const [interestedFile, setInterestedFile] = useState<string | null>(null);
  const [interestedRiders, setInterestedRiders] = useState<ParsedRider[]>([]);
  const [interestedError, setInterestedError] = useState<string | null>(null);

  // School import state
  const [schoolFile, setSchoolFile] = useState<string | null>(null);
  const [inquiries, setInquiries] = useState<SchoolInquiry[]>([]);
  const [schoolError, setSchoolError] = useState<string | null>(null);

  // Save state
  const [saving, setSaving] = useState(false);

  // Preview roster state
  const [rosterData, setRosterData] = useState<Record<string, unknown> | null>(null);
  const [rosterLoading, setRosterLoading] = useState(false);

  // Redirect non-admins
  useEffect(() => {
    if (!authLoading && role !== 'admin') navigate('/dashboard');
  }, [authLoading, role, navigate]);

  // Load org
  useEffect(() => {
    if (!id || authLoading || role !== 'admin') return;
    const fetchOrg = async () => {
      try {
        setLoading(true);
        const snap = await getDoc(doc(db, 'organizations', id));
        if (!snap.exists()) {
          toast({ title: 'Organization not found', type: 'error' });
          navigate('/admin/organizations');
          return;
        }
        const data = snap.data();
        setOrg({
          id: snap.id,
          name: data.name ?? '',
          type: data.type ?? 'school',
          slug: data.slug ?? '',
          season: data.season ?? '',
          description: data.description ?? '',
          contactEmail: data.contactEmail ?? '',
          accessCode: data.accessCode ?? '',
          createdAt: (data.createdAt as Timestamp)?.toDate() ?? new Date(),
          updatedAt: (data.updatedAt as Timestamp)?.toDate() ?? new Date(),
        });
      } catch (err) {
        console.error('Failed to load org:', err);
        toast({ title: 'Failed to load organization', type: 'error' });
      } finally {
        setLoading(false);
      }
    };
    fetchOrg();
  }, [id, authLoading, role]);

  // Load roster for preview tab
  useEffect(() => {
    if (activeTab !== 'preview' || !id) return;
    const fetchRoster = async () => {
      try {
        setRosterLoading(true);
        const snap = await getDoc(doc(db, 'organizations', id, 'rosters', 'current'));
        setRosterData(snap.exists() ? snap.data() as Record<string, unknown> : null);
      } catch (err) {
        console.error('Failed to load roster:', err);
      } finally {
        setRosterLoading(false);
      }
    };
    fetchRoster();
  }, [activeTab, id]);

  const handleCSVFile = (
    file: File,
    parser: (rows: Record<string, string>[]) => unknown[],
    onResult: (result: unknown[], fileName: string) => void,
    onError: (err: string) => void,
  ) => {
    const reader = new FileReader();
    reader.onload = e => {
      try {
        const text = e.target?.result as string;
        const rows = parseCSV(text);
        const result = parser(rows);
        onResult(result, file.name);
        onError('');
      } catch {
        onError('Failed to parse CSV. Please check the format.');
      }
    };
    reader.readAsText(file);
  };

  const handleSaveCamp = async () => {
    if (!id) return;
    if (bookedRiders.length === 0 && interestedRiders.length === 0) {
      toast({ title: 'Upload at least one CSV first', type: 'error' });
      return;
    }
    try {
      setSaving(true);
      await setDoc(doc(db, 'organizations', id, 'rosters', 'current'), {
        bookedRiders,
        interestedRiders,
        type: 'camp',
        updatedAt: serverTimestamp(),
      });
      toast({ title: 'Roster saved', type: 'success' });
    } catch (err) {
      console.error('Failed to save roster:', err);
      toast({ title: 'Failed to save roster', type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleSaveSchool = async () => {
    if (!id) return;
    if (inquiries.length === 0) {
      toast({ title: 'Upload a CSV first', type: 'error' });
      return;
    }
    try {
      setSaving(true);
      await setDoc(doc(db, 'organizations', id, 'rosters', 'current'), {
        inquiries,
        type: 'school',
        updatedAt: serverTimestamp(),
      });
      toast({ title: 'Roster saved', type: 'success' });
    } catch (err) {
      console.error('Failed to save roster:', err);
      toast({ title: 'Failed to save roster', type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const shareUrl = org
    ? `${window.location.origin}/school/${org.slug}?code=${org.accessCode}`
    : '';

  const handleCopy = () => {
    navigator.clipboard.writeText(shareUrl).then(() => {
      toast({ title: 'Copied to clipboard', type: 'success' });
    });
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-shuttlebee-yellow border-t-transparent rounded-full animate-spin" />
          <p className="text-gray-600">Loading…</p>
        </div>
      </div>
    );
  }

  if (!org) return null;

  const WEEKS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];

  return (
    <div className="max-w-5xl mx-auto px-4 py-8">
      {/* Back + header */}
      <div className="mb-6">
        <button
          onClick={() => navigate('/admin/organizations')}
          className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-800 mb-3 transition-colors"
        >
          <ChevronLeft className="w-4 h-4" />
          All Organizations
        </button>
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{org.name}</h1>
            <div className="flex items-center gap-2 mt-1">
              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                org.type === 'school' ? 'bg-blue-100 text-blue-800' : 'bg-green-100 text-green-800'
              }`}>
                {org.type}
              </span>
              {org.season && (
                <span className="text-sm text-gray-500">{org.season}</span>
              )}
            </div>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => window.open(shareUrl, '_blank')}
            className="flex items-center gap-1"
          >
            <ExternalLink className="w-4 h-4" />
            View Live Report
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-gray-200 mb-6 gap-1">
        {(['overview', 'import', 'preview'] as TabId[]).map(tab => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`px-4 py-2 text-sm font-medium capitalize transition-colors border-b-2 -mb-px ${
              activeTab === tab
                ? 'border-primary text-primary'
                : 'border-transparent text-gray-500 hover:text-gray-800'
            }`}
          >
            {tab === 'preview' ? 'Report Preview' : tab.charAt(0).toUpperCase() + tab.slice(1)}
          </button>
        ))}
      </div>

      {/* ── Overview Tab ───────────────────────────────────────────────────────── */}
      {activeTab === 'overview' && (
        <div className="flex flex-col gap-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Details</CardTitle>
            </CardHeader>
            <CardContent>
              <dl className="grid grid-cols-1 sm:grid-cols-2 gap-y-4 gap-x-8 text-sm">
                <div>
                  <dt className="text-gray-500 font-medium">Name</dt>
                  <dd className="text-gray-900 mt-0.5">{org.name}</dd>
                </div>
                <div>
                  <dt className="text-gray-500 font-medium">Type</dt>
                  <dd className="text-gray-900 mt-0.5 capitalize">{org.type}</dd>
                </div>
                <div>
                  <dt className="text-gray-500 font-medium">Season</dt>
                  <dd className="text-gray-900 mt-0.5">{org.season || '—'}</dd>
                </div>
                <div>
                  <dt className="text-gray-500 font-medium">Contact Email</dt>
                  <dd className="text-gray-900 mt-0.5">{org.contactEmail || '—'}</dd>
                </div>
                <div className="sm:col-span-2">
                  <dt className="text-gray-500 font-medium">Description</dt>
                  <dd className="text-gray-900 mt-0.5">{org.description || '—'}</dd>
                </div>
                <div>
                  <dt className="text-gray-500 font-medium">Slug</dt>
                  <dd className="text-gray-900 mt-0.5 font-mono text-xs">{org.slug}</dd>
                </div>
              </dl>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Shareable Access Link</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-xs text-gray-500 mb-3">Share this URL with the organization. The access code is embedded — no separate login needed.</p>
              <div className="flex items-center gap-2">
                <div className="flex-1 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm font-mono text-gray-700 truncate">
                  {shareUrl}
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleCopy}
                  className="flex items-center gap-1 flex-shrink-0"
                >
                  <Copy className="w-4 h-4" />
                  Copy
                </Button>
              </div>
              <div className="mt-3">
                <Button
                  variant="shuttlebee"
                  size="sm"
                  onClick={() => window.open(shareUrl, '_blank')}
                  className="flex items-center gap-2"
                >
                  <ExternalLink className="w-4 h-4" />
                  View Live Report
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* ── Import Tab ─────────────────────────────────────────────────────────── */}
      {activeTab === 'import' && (
        <div>
          {org.type === 'camp' ? (
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Import Camp Roster</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-gray-500 mb-4">
                  Upload the booked students CSV and/or interested families CSV.
                </p>
                <div className="flex flex-col sm:flex-row gap-4">
                  <UploadZone
                    label="Booked Students"
                    description="Guardian Name, Guardian Email, Pickup Location, Enrolled Weeks…"
                    fileName={bookedFile}
                    rowCount={bookedRiders.length}
                    error={bookedError}
                    color="blue"
                    onFile={file =>
                      handleCSVFile(
                        file,
                        rows => parseBookedCSV(rows),
                        (result, name) => { setBookedRiders(result as ParsedRider[]); setBookedFile(name); },
                        setBookedError,
                      )
                    }
                    onClear={() => { setBookedFile(null); setBookedRiders([]); setBookedError(null); }}
                  />
                  <UploadZone
                    label="Interested Families"
                    description="Recipient Name, Recipient Email, Pickup Location, Selected Weeks…"
                    fileName={interestedFile}
                    rowCount={interestedRiders.length}
                    error={interestedError}
                    color="green"
                    onFile={file =>
                      handleCSVFile(
                        file,
                        rows => parseInterestedCSV(rows),
                        (result, name) => { setInterestedRiders(result as ParsedRider[]); setInterestedFile(name); },
                        setInterestedError,
                      )
                    }
                    onClear={() => { setInterestedFile(null); setInterestedRiders([]); setInterestedError(null); }}
                  />
                </div>

                {/* Preview table */}
                {bookedRiders.length > 0 && (
                  <RiderPreviewTable riders={bookedRiders} label="Booked Students" />
                )}
                {interestedRiders.length > 0 && (
                  <RiderPreviewTable riders={interestedRiders} label="Interested Families" />
                )}

                {(bookedRiders.length > 0 || interestedRiders.length > 0) && (
                  <div className="mt-5">
                    <Button variant="shuttlebee" onClick={handleSaveCamp} disabled={saving}>
                      {saving ? 'Saving…' : `Save Roster (${bookedRiders.length + interestedRiders.length} records)`}
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Import School Inquiries</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-gray-500 mb-4">
                  Upload the school inquiries CSV (Concordia Prep format).
                </p>
                <UploadZone
                  label="Inquiries CSV"
                  description="Student First Name, Student Last Name, Grade, Guardian Name, Guardian Email…"
                  fileName={schoolFile}
                  rowCount={inquiries.length}
                  error={schoolError}
                  color="blue"
                  onFile={file =>
                    handleCSVFile(
                      file,
                      rows => parseSchoolCSV(rows),
                      (result, name) => { setInquiries(result as SchoolInquiry[]); setSchoolFile(name); },
                      setSchoolError,
                    )
                  }
                  onClear={() => { setSchoolFile(null); setInquiries([]); setSchoolError(null); }}
                />

                {inquiries.length > 0 && (
                  <>
                    <InquiryPreviewTable inquiries={inquiries} />
                    <div className="mt-5">
                      <Button variant="shuttlebee" onClick={handleSaveSchool} disabled={saving}>
                        {saving ? 'Saving…' : `Save ${inquiries.length} Inquiries`}
                      </Button>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* ── Report Preview Tab ─────────────────────────────────────────────────── */}
      {activeTab === 'preview' && (
        <div>
          {rosterLoading ? (
            <div className="flex justify-center py-12">
              <div className="w-8 h-8 border-4 border-shuttlebee-yellow border-t-transparent rounded-full animate-spin" />
            </div>
          ) : !rosterData ? (
            <Card>
              <CardContent className="py-12 text-center">
                <p className="text-gray-500 text-sm">No data imported yet. Go to the Import tab to upload a roster.</p>
              </CardContent>
            </Card>
          ) : rosterData.type === 'camp' ? (
            <CampReportPreview
              org={org}
              bookedRiders={(rosterData.bookedRiders as ParsedRider[]) ?? []}
              interestedRiders={(rosterData.interestedRiders as ParsedRider[]) ?? []}
              weeks={WEEKS}
            />
          ) : (
            <SchoolReportPreview
              org={org}
              inquiries={(rosterData.inquiries as SchoolInquiry[]) ?? []}
            />
          )}
        </div>
      )}
    </div>
  );
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function RiderPreviewTable({ riders, label }: { riders: ParsedRider[]; label: string }) {
  if (riders.length === 0) return null;
  return (
    <div className="mt-4">
      <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
        {label} — {riders.length} records
      </div>
      <div className="overflow-x-auto rounded-lg border border-gray-200">
        <table className="w-full text-xs">
          <thead>
            <tr className="bg-gray-50 border-b border-gray-200">
              <th className="text-left px-3 py-2 font-semibold text-gray-700">Name</th>
              <th className="text-left px-3 py-2 font-semibold text-gray-700">Email</th>
              <th className="text-left px-3 py-2 font-semibold text-gray-700">Pickup</th>
              <th className="text-center px-3 py-2 font-semibold text-gray-700">Students</th>
              <th className="text-center px-3 py-2 font-semibold text-gray-700">Weeks</th>
              <th className="text-center px-3 py-2 font-semibold text-gray-700">Loop</th>
            </tr>
          </thead>
          <tbody>
            {riders.map((r, i) => (
              <tr key={i} className="border-b border-gray-100 hover:bg-gray-50">
                <td className="px-3 py-2 text-gray-900">{r.name || '—'}</td>
                <td className="px-3 py-2 text-gray-600">{r.email}</td>
                <td className="px-3 py-2 text-gray-600">{r.pickup || '—'}</td>
                <td className="px-3 py-2 text-center text-gray-900">{r.students}</td>
                <td className="px-3 py-2 text-center text-gray-600">
                  {r.weeks.length > 0 ? r.weeks.join(', ') : 'TBD'}
                </td>
                <td className="px-3 py-2 text-center">
                  <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${
                    r.loop === 'late' ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'
                  }`}>
                    {r.loop}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function InquiryPreviewTable({ inquiries }: { inquiries: SchoolInquiry[] }) {
  return (
    <div className="mt-4">
      <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
        Inquiries — {inquiries.length} records
      </div>
      <div className="overflow-x-auto rounded-lg border border-gray-200">
        <table className="w-full text-xs">
          <thead>
            <tr className="bg-gray-50 border-b border-gray-200">
              <th className="text-left px-3 py-2 font-semibold text-gray-700">Student</th>
              <th className="text-left px-3 py-2 font-semibold text-gray-700">Grade</th>
              <th className="text-left px-3 py-2 font-semibold text-gray-700">Guardian</th>
              <th className="text-left px-3 py-2 font-semibold text-gray-700">Email</th>
              <th className="text-left px-3 py-2 font-semibold text-gray-700">Pickup</th>
              <th className="text-left px-3 py-2 font-semibold text-gray-700">Days/Weeks</th>
            </tr>
          </thead>
          <tbody>
            {inquiries.map((r, i) => (
              <tr key={i} className="border-b border-gray-100 hover:bg-gray-50">
                <td className="px-3 py-2 text-gray-900">{r.studentFirstName} {r.studentLastName}</td>
                <td className="px-3 py-2 text-gray-600">{r.grade || '—'}</td>
                <td className="px-3 py-2 text-gray-600">{r.guardianName || '—'}</td>
                <td className="px-3 py-2 text-gray-600">{r.guardianEmail}</td>
                <td className="px-3 py-2 text-gray-600">{r.pickupAddress || '—'}</td>
                <td className="px-3 py-2 text-gray-600">{r.daysOrWeeks || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function CampReportPreview({
  org,
  bookedRiders,
  interestedRiders,
  weeks,
}: {
  org: Org;
  bookedRiders: ParsedRider[];
  interestedRiders: ParsedRider[];
  weeks: number[];
}) {
  const studentsOnLoopWeek = (loop: 'early' | 'late', week: number, riders: ParsedRider[]) =>
    riders.filter(r => r.loop === loop && r.weeks.includes(week)).reduce((s, r) => s + r.students, 0);

  return (
    <div className="flex flex-col gap-6">
      {/* Summary */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">{org.name} — {org.season}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-center">
            <div>
              <div className="text-2xl font-bold text-blue-600">{bookedRiders.length}</div>
              <div className="text-xs text-gray-500">Booked Families</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-green-600">{interestedRiders.length}</div>
              <div className="text-xs text-gray-500">Interested Families</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-gray-800">
                {bookedRiders.reduce((s, r) => s + r.students, 0)}
              </div>
              <div className="text-xs text-gray-500">Total Booked Students</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-gray-800">
                {interestedRiders.reduce((s, r) => s + r.students, 0)}
              </div>
              <div className="text-xs text-gray-500">Total Interested Students</div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Weekly grid */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Weekly Enrollment Grid</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-xs text-center">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left px-3 py-2 font-semibold text-gray-700">Loop</th>
                  {weeks.map(w => (
                    <th key={w} className="px-2 py-2 font-semibold text-gray-600">Wk {w}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {(['early', 'late'] as const).map(loop => (
                  <tr key={loop} className="border-b border-gray-100">
                    <td className="text-left px-3 py-2 font-medium text-gray-700 capitalize">{loop}</td>
                    {weeks.map(w => {
                      const count = studentsOnLoopWeek(loop, w, bookedRiders);
                      return (
                        <td key={w} className={`px-2 py-2 font-semibold ${count > 0 ? 'text-green-700' : 'text-gray-300'}`}>
                          {count > 0 ? count : '—'}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function SchoolReportPreview({ org, inquiries }: { org: Org; inquiries: SchoolInquiry[] }) {
  return (
    <div className="flex flex-col gap-6">
      <Card>
        <CardHeader>
          <CardTitle className="text-base">{org.name} — {org.season}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-4 text-center mb-4">
            <div>
              <div className="text-2xl font-bold text-blue-600">{inquiries.length}</div>
              <div className="text-xs text-gray-500">Total Inquiries</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-gray-800">
                {new Set(inquiries.map(r => r.pickupZipcode).filter(Boolean)).size}
              </div>
              <div className="text-xs text-gray-500">Unique Zip Codes</div>
            </div>
          </div>
          <InquiryPreviewTable inquiries={inquiries} />
        </CardContent>
      </Card>
    </div>
  );
}
